import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '../../stores/languageStore'
import { EnvironmentBadge as EnvironmentBadgeType } from '../../utils/environmentBadges'

interface EnvironmentBadgeProps {
  badge: EnvironmentBadgeType
  className?: string
}

const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({ badge, className = '' }) => {
  const { t } = useTranslation()
  const { language } = useLanguageStore()
  const isRTL = language === 'ar' || language === 'he'

  const getBadgeConfig = (type: 'beta' | 'dev') => {
    switch (type) {
      case 'beta':
        return {
          gradient: 'from-purple-400 via-pink-400 to-purple-500',
          icon: '🚀',
          text: t('environment.beta', 'Beta'),
          tailColor: 'border-t-purple-400'
        }
      case 'dev':
        return {
          gradient: 'from-green-400 via-emerald-400 to-green-500',
          icon: '🔧',
          text: t('environment.dev', 'Dev'),
          tailColor: 'border-t-green-400'
        }
      default:
        return {
          gradient: 'from-gray-400 via-gray-500 to-gray-600',
          icon: '⚙️',
          text: type,
          tailColor: 'border-t-gray-400'
        }
    }
  }

  const config = getBadgeConfig(badge.type)

  return (
    <div className={`relative ${className}`}>
      {/* Ribbon background with shadow - smaller size but still readable */}
      <div className={`bg-gradient-to-r ${config.gradient} text-white text-xs font-bold px-2 py-1 rounded shadow-md transform ${isRTL ? '-rotate-2' : 'rotate-2'} hover:rotate-0 transition-transform duration-200`}>
        <div className="flex items-center gap-1">
          <span className="text-white opacity-90 text-xs">{config.icon}</span>
          <span className="text-xs font-semibold">{config.text}</span>
        </div>
      </div>
      {/* Smaller ribbon tail effect */}
      <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} w-0 h-0 border-l-2 border-r-2 border-t-3 border-transparent ${config.tailColor} transform ${isRTL ? '-rotate-2' : 'rotate-2'}`}></div>
    </div>
  )
}

export default EnvironmentBadge